import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

export default function ForgotPasswordPage() {
  const { tenantPath } = useTenant();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await api.post('/auth/forgot-password', { email });
      
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      
      setIsSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.response?.status === 429) {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.success('If the email exists, a password reset link has been sent');
        setIsSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <img src="/logo.png" alt="Golfshop.fit" className="h-20 w-20 mx-auto mb-4 rounded-full object-cover" data-testid="forgot-password-title" />
          <p className="text-gray-600 text-lg">Reset your password</p>
        </div>
        
        <div className="bg-gray-50 p-8 rounded-lg border border-[#F3F4F6]">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-600 text-sm mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
                <p className="text-gray-500 text-xs mt-2">
                  Enter your email and we'll send you a link to reset your password
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#334155]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#334155]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Check Your Email</h3>
              <p className="text-gray-600">
                If an account exists for {email}, you will receive a password reset link shortly.
              </p>
              
              {resetToken && (
                <div className="mt-6 p-4 bg-gray-100 border border-[#F3F4F6] rounded-lg text-left">
                  <p className="text-xs text-[#334155] font-bold uppercase tracking-wider mb-2">Development Mode</p>
                  <p className="text-gray-900 text-sm mb-2">Reset Token:</p>
                  <code className="text-xs text-gray-600 break-all">{resetToken}</code>
                  <Link 
                    to={tenantPath(`/reset-password?token=${resetToken}`)}
                    className="block mt-3 text-[#334155] hover:text-[#475569] text-sm font-medium transition-colors"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}
            </div>
          )}
          
          <Link 
            to={tenantPath('/login')}
            className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-900 text-sm mt-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to login</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
